import { TERMINAL_FAILURE_RECORD_STATUSES } from '@aica/api-types';
import { REASON_CHAT_TOO_LONG } from '@aica/core';
import type { RecordStatus } from '@aica/api-types';

export const SUCCESSFUL_RECORD_STATUSES = [
  'completed',
  'delivered',
  'partially_delivered',
  'pii_redacted',
] as const satisfies readonly RecordStatus[];

/**
 * Re-export, not a copy. `@aica/database` groups the same statuses for the
 * dashboard SQL (`repositories/record-status-sets.ts`); the two lists used to
 * be maintained separately, and a status added on one side only made "Total
 * failed" stop matching the sum of the tiles built from this one.
 */
export const FAILED_RECORD_STATUSES = TERMINAL_FAILURE_RECORD_STATUSES;

export const PENDING_RECORD_STATUSES = [
  'pending',
  'awaiting_media',
  'awaiting_chat_completion',
  // Запись принята, но ждёт решения владельца или пополнения кошелька. Без
  // них обе категории выпадали бы из счётчиков дашборда: не успех, не сбой,
  // не в работе — то есть нигде.
  'awaiting_start',
  'awaiting_funds',
] as const satisfies readonly RecordStatus[];

/**
 * Accepted and stored, but deliberately not processed — either waiting on a
 * person (`awaiting_funds`/`awaiting_start`, nothing moves until someone tops
 * up or flips the switch) or already decided closed (`not_applicable` — a
 * missed call, an abandoned chat, a recording confirmed absent at its source,
 * and so on). Distinct from PENDING (queued work) and from a plain terminal
 * failure: nothing here is retried, and nothing here is broken.
 *
 * `not_applicable` joined this set once `RecordHaltedNotice` learned to
 * explain it (see that component) — before, every `not_applicable` record
 * fell through to a generic "processing may still be in progress" message,
 * which is false for a status the pipeline will never revisit.
 */
export const HALTED_RECORD_STATUSES = [
  'awaiting_funds',
  'awaiting_start',
  'not_applicable',
] as const satisfies readonly RecordStatus[];

export const PROCESSING_RECORD_STATUSES = [
  'transcribing',
  'transcribed',
  'analyzing',
  'analyzed',
  'delivering',
] as const satisfies readonly RecordStatus[];

/** Statuses that represent special terminal outcomes (not success/failure) */
export const TERMINAL_SPECIAL_RECORD_STATUSES = [
  'not_applicable',
  'processing_objected',
] as const satisfies readonly RecordStatus[];

export const TRANSCRIPT_READY_RECORD_STATUSES = [
  'transcribed',
  'analyzing',
  'analyzed',
  'delivering',
  'delivered',
  'completed',
] as const satisfies readonly RecordStatus[];

/**
 * Статусы, на которых бэкенд принимает переанализ.
 *
 * Зеркало `REANALYZEABLE_STATUSES` из
 * `apps/edge-api/src/routes/records/processing.ts` — там SSOT, и расхождение
 * видно по тесту `record-detail-reanalyze.test.ts`. Нужно здесь потому, что
 * панель детали должна СПРАШИВАТЬ транскрипт для тех же статусов, для которых
 * бэкенд готов переанализировать: `TRANSCRIPT_READY_RECORD_STATUSES` перечисляет
 * успешный путь и исключает как раз три сбойных, у которых транскрипт обычно уже
 * есть (анализ падает ПОСЛЕ распознавания).
 */
export const REANALYZEABLE_RECORD_STATUSES = [
  'delivered',
  'completed',
  'delivery_failed',
  'analysis_failed',
  'failed',
] as const satisfies readonly RecordStatus[];

const SUCCESSFUL_RECORD_STATUS_SET = new Set<string>(SUCCESSFUL_RECORD_STATUSES);
const FAILED_RECORD_STATUS_SET = new Set<string>(FAILED_RECORD_STATUSES);
const PENDING_RECORD_STATUS_SET = new Set<string>(PENDING_RECORD_STATUSES);
const PROCESSING_RECORD_STATUS_SET = new Set<string>(PROCESSING_RECORD_STATUSES);
const HALTED_RECORD_STATUS_SET = new Set<string>(HALTED_RECORD_STATUSES);
const TERMINAL_SPECIAL_RECORD_STATUS_SET = new Set<string>(
  TERMINAL_SPECIAL_RECORD_STATUSES
);
const TRANSCRIPT_READY_RECORD_STATUS_SET = new Set<string>(
  TRANSCRIPT_READY_RECORD_STATUSES
);
const REANALYZEABLE_RECORD_STATUS_SET = new Set<string>(REANALYZEABLE_RECORD_STATUSES);

export function isSuccessfulRecordStatus(status?: string | null): boolean {
  return !!status && SUCCESSFUL_RECORD_STATUS_SET.has(status);
}

export function isFailedRecordStatus(status?: string | null): boolean {
  return !!status && FAILED_RECORD_STATUS_SET.has(status);
}

export function isPendingRecordStatus(status?: string | null): boolean {
  return !!status && PENDING_RECORD_STATUS_SET.has(status);
}

export function isProcessingRecordStatus(status?: string | null): boolean {
  return !!status && PROCESSING_RECORD_STATUS_SET.has(status);
}

export function isHaltedRecordStatus(status?: string | null): boolean {
  return !!status && HALTED_RECORD_STATUS_SET.has(status);
}

export function isTerminalSpecialRecordStatus(status?: string | null): boolean {
  return !!status && TERMINAL_SPECIAL_RECORD_STATUS_SET.has(status);
}

export function isTranscriptReadyRecordStatus(status?: string | null): boolean {
  return !!status && TRANSCRIPT_READY_RECORD_STATUS_SET.has(status);
}

export function isReanalyzeableRecordStatus(status?: string | null): boolean {
  return !!status && REANALYZEABLE_RECORD_STATUS_SET.has(status);
}

/**
 * Meridian badge classes for a record status, keyed by status family.
 * Shared by every dashboard that renders a status pill (admin + client ops
 * tabs) so the color mapping lives in exactly one place.
 */
export function getRecordStatusBadgeClass(status: string): string {
  if (isSuccessfulRecordStatus(status)) return 'bg-success-dim text-success-fg';
  if (isProcessingRecordStatus(status)) return 'bg-primary-dim text-primary-fg';
  if (isPendingRecordStatus(status)) return 'bg-warning-dim text-warning-fg';
  if (isFailedRecordStatus(status)) return 'bg-danger-dim text-danger-fg';
  return 'bg-bg-subtle/10 text-fg-subtle';
}

/**
 * A record status as the records list names it, in the interface language.
 *
 * Every dashboard shows statuses through this one label set
 * (`records:records.processingStatus.*`, every status in all four
 * languages). The client overview used to keep a second, dashboard-only set
 * that missed `awaiting_start`, `awaiting_funds`, `partially_delivered` and
 * `processing_objected`, and the admin overview printed the raw enum.
 */
export function recordStatusLabel(
  t: (key: string, options: { defaultValue: string }) => string,
  status: string
): string {
  return t(`records:records.processingStatus.${status.toLowerCase()}`, {
    defaultValue: status,
  });
}

/**
 * A record a retry left waiting for a start (#1264): `awaiting_start` with
 * results of an earlier run — an analysis, or a call's transcript (ASR ran).
 * A chat's `transcriptPreview` proves nothing: an uploaded chat carries it
 * from intake, before any run (code review on #1264).
 */
export function isRestartPendingRecord(
  record:
    | {
        status?: string | null;
        type?: string | null;
        analysisId?: string | null;
        transcriptPreview?: string | null;
      }
    | null
    | undefined
): boolean {
  if (record?.status !== 'awaiting_start') return false;
  if (record.analysisId) return true;
  return record.type !== 'chat' && Boolean(record.transcriptPreview);
}

/**
 * A chat closed for being longer than the analysis cap (#1489). Unlike the
 * other `not_applicable` closes it HAS a transcript — the pipeline saved it
 * before refusing to analyse it — and the notice tells the customer it stays
 * available, so the detail panel asks for it.
 */
export function isChatTooLongRecord(
  record: { status?: string | null; errorMessage?: string | null } | null | undefined
): boolean {
  return (
    record?.status === 'not_applicable' && record.errorMessage === REASON_CHAT_TOO_LONG
  );
}
